export interface RealtimeFunctionCall {
  callId: string;
  name: string;
  argumentsJson: string;
}

export type RealtimeServerEvent =
  | { type: 'speech_started'; itemId: string }
  | { type: 'speech_stopped' }
  | {
      type: 'user_transcript';
      itemId: string;
      transcript: string;
      averageLogProbability: number | null;
    }
  | { type: 'user_transcript_failed'; itemId: string }
  | { type: 'assistant_delta'; itemId: string; delta: string }
  | { type: 'assistant_text'; itemId: string; text: string }
  | { type: 'response_started' }
  | { type: 'audio_started' }
  | { type: 'audio_stopped' }
  | { type: 'response_done'; status: string; functionCalls: RealtimeFunctionCall[] }
  | { type: 'error'; code: string }
  | { type: 'ignored' };

const IGNORED: RealtimeServerEvent = { type: 'ignored' };

const CANCELLED_RESPONSE_STATUS = 'cancelled';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function readText(record: Record<string, unknown>, key: string): string {
  const value = record[key];
  return typeof value === 'string' ? value : '';
}

function averageLogProbabilityOf(logprobs: unknown): number | null {
  if (!Array.isArray(logprobs) || logprobs.length === 0) {
    return null;
  }
  let total = 0;
  for (const token of logprobs) {
    if (!isRecord(token) || typeof token.logprob !== 'number') {
      return null;
    }
    total = total + token.logprob;
  }
  return total / logprobs.length;
}

function functionCallsOf(response: unknown): RealtimeFunctionCall[] {
  if (!isRecord(response) || !Array.isArray(response.output)) {
    return [];
  }
  if (response.status === CANCELLED_RESPONSE_STATUS) {
    return [];
  }
  const calls: RealtimeFunctionCall[] = [];
  for (const item of response.output) {
    if (isRecord(item) && item.type === 'function_call') {
      calls.push({
        callId: readText(item, 'call_id'),
        name: readText(item, 'name'),
        argumentsJson: readText(item, 'arguments') || '{}',
      });
    }
  }
  return calls;
}

export function parseRealtimeServerEvent(rawMessage: string): RealtimeServerEvent {
  let parsed: unknown;
  try {
    parsed = JSON.parse(rawMessage);
  } catch {
    return IGNORED;
  }
  if (!isRecord(parsed)) {
    return IGNORED;
  }
  switch (parsed.type) {
    case 'input_audio_buffer.speech_started':
      return { type: 'speech_started', itemId: readText(parsed, 'item_id') };
    case 'input_audio_buffer.speech_stopped':
      return { type: 'speech_stopped' };
    case 'conversation.item.input_audio_transcription.completed':
      return {
        type: 'user_transcript',
        itemId: readText(parsed, 'item_id'),
        transcript: readText(parsed, 'transcript'),
        averageLogProbability: averageLogProbabilityOf(parsed.logprobs),
      };
    case 'conversation.item.input_audio_transcription.failed':
      return { type: 'user_transcript_failed', itemId: readText(parsed, 'item_id') };
    case 'response.output_audio_transcript.delta':
      return {
        type: 'assistant_delta',
        itemId: readText(parsed, 'item_id'),
        delta: readText(parsed, 'delta'),
      };
    case 'response.output_audio_transcript.done':
      return {
        type: 'assistant_text',
        itemId: readText(parsed, 'item_id'),
        text: readText(parsed, 'transcript'),
      };
    case 'response.created':
      return { type: 'response_started' };
    case 'output_audio_buffer.started':
      return { type: 'audio_started' };
    case 'output_audio_buffer.stopped':
      return { type: 'audio_stopped' };
    case 'response.done':
      return {
        type: 'response_done',
        status: isRecord(parsed.response) ? readText(parsed.response, 'status') : '',
        functionCalls: functionCallsOf(parsed.response),
      };
    case 'error':
      return {
        type: 'error',
        code: isRecord(parsed.error) ? readText(parsed.error, 'code') : '',
      };
    default:
      return IGNORED;
  }
}
